'use client';

import { useEffect } from 'react';
import {
  ensureAudioReady,
  isMusicMuted,
  startMusicMuteCrossTabSync,
} from '@/lib/sounds';
import { getMusicPlayer } from './music-player';

const MOBILE_QUERY = '(max-width: 768px)';

/**
 * Headless component that boots the music singleton.
 *
 * Browsers suspend playback until a user gesture (Chrome / Safari's
 * autoplay policy). We attach one-shot `pointerdown` + `keydown`
 * listeners on the document: the first gesture of the session calls
 * `start()` on the singleton, kicking off the playlist at volume zero
 * and ramping up.
 *
 * **Mobile never plays music.** The mobile UX explicitly omits the
 * music toggle from `LobbyFooter` (it's hidden below `md`), so on
 * phones we also skip the gesture wiring entirely — any tap on a
 * tile would otherwise boot the player and the user has no way to
 * silence it. We key off `matchMedia('(max-width: 768px)')`, which
 * matches `useIsMobile` and the same `md` Tailwind breakpoint that
 * hides the music pill. The media query is re-evaluated on change so
 * a tablet rotating portrait ↔ landscape (or a desktop window being
 * narrowed) flips the gesture wiring on and off cleanly.
 *
 * **Single-tab playback.** We still ask the `MusicPlayer` for an
 * exclusive Web Lock so only the leader tab drives real audio —
 * follower tabs stay silent. This part is always wired; leadership
 * acquisition is cheap and keeps the tab eligible to own playback
 * when/if it reaches a desktop width.
 *
 * The `music-mute-changed` listener is wired only alongside the
 * gesture handler (i.e. on non-mobile), because the only UI that
 * dispatches that event is the desktop-only music pill in the footer.
 */
export function MusicController() {
  useEffect(() => {
    const player = getMusicPlayer();
    let cancelled = false;
    let onGesture: (() => void) | null = null;
    let onMuteChange: (() => void) | null = null;

    const wireHandlers = () => {
      if (cancelled) return;
      if (onGesture || onMuteChange) return;

      // The gesture handler retries `player.start()` on every gesture
      // until the player is definitively in its target state — playing,
      // or settled as a muted leader. The previous version removed
      // itself unconditionally after the first call, which silently
      // broke playback in three real cases:
      //
      //   1. Multi-tab: the first click happens on a follower tab.
      //      `start()` short-circuits because `hasLeadership` is false.
      //      The handler self-removes. Even after the leader tab closes
      //      and this tab becomes leader, no future gesture can boot
      //      the player — music stays silent forever.
      //
      //   2. Tutorial-first session: the user lands on `/tutorial`,
      //      the input guard captures every body click, and the only
      //      bubble-phase gestures that reach `window` are the ones
      //      targeting the overlay portal / audio pills (which the
      //      guard explicitly allows). Those pointerdowns hit `start()`
      //      while the audio element is still warming up; if the
      //      user then exits the tutorial, no more retries fire and
      //      music never plays site-wide.
      //
      //   3. Mobile-narrowed desktop: viewport flips mobile, handlers
      //      are unwired; flips back; first gesture starts but the
      //      `<audio>.play()` rejects (e.g. an interleaved focus loss).
      //      Old code lost the only retry slot.
      //
      // Keeping the handler attached costs almost nothing — `start()`
      // is idempotent (same activeEl, same play() promise) and we
      // bail straight out as soon as the state shows we're done.
      onGesture = () => {
        const state = player.getState();
        const muted = isMusicMuted();
        const settled =
          state.playing ||
          (muted && state.ready && player.isLeader());
        if (settled) {
          if (onGesture) {
            window.removeEventListener('pointerdown', onGesture);
            window.removeEventListener('keydown', onGesture);
            window.removeEventListener('touchstart', onGesture);
            onGesture = null;
          }
          return;
        }
        if (!muted) {
          void player.start();
        } else {
          void player.start().then(() => void player.applyMute(true));
        }
      };
      window.addEventListener('pointerdown', onGesture, { passive: true });
      window.addEventListener('keydown', onGesture, { once: false });
      window.addEventListener('touchstart', onGesture, { passive: true });

      onMuteChange = () => {
        void player.applyMute(isMusicMuted());
      };
      window.addEventListener('music-mute-changed', onMuteChange);
    };

    const unwireHandlers = () => {
      if (onGesture) {
        window.removeEventListener('pointerdown', onGesture);
        window.removeEventListener('keydown', onGesture);
        window.removeEventListener('touchstart', onGesture);
        onGesture = null;
      }
      if (onMuteChange) {
        window.removeEventListener('music-mute-changed', onMuteChange);
        onMuteChange = null;
      }
    };

    void player.acquireLeadership();

    // Cross-tab mute sync. `setMusicMuted` only fires a same-window
    // CustomEvent, so without this bridge a toggle in any tab other
    // than the music-leader tab would flip localStorage + the local
    // UI but never reach the leader's `<audio>` element — the user
    // would hear nothing change despite the pill animating, and
    // would report "toggling music on/off doesn't appear to work."
    //
    // Wired *outside* `wireHandlers()` because the bridge needs to
    // run on every viewport (mobile profiles still benefit from
    // their pill UI staying in lockstep with desktop tabs even
    // though they don't drive playback themselves), and is therefore
    // independent of the gesture/mute-listener pair that
    // `wireHandlers` toggles when the viewport crosses the mobile
    // breakpoint.
    const stopMuteCrossTabSync = startMusicMuteCrossTabSync();

    // SFX primer — separate from the music gesture wiring because
    // mobile deliberately skips music autoplay but still needs the
    // SFX WebAudio context unlocked (drawers, tile taps, etc.).
    // iOS / Safari keep the AudioContext `suspended` until the first
    // user gesture *from an event handler* creates or resumes one;
    // calling `ensureAudioReady()` inside a real pointerdown/touchstart
    // satisfies that, and subsequent SFX calls can run from any
    // async callback (e.g. a React click handler that opens a drawer
    // two frames later). One-shot — we only need to thaw it once.
    const primeSfx = () => {
      ensureAudioReady();
      window.removeEventListener('pointerdown', primeSfx);
      window.removeEventListener('touchstart', primeSfx);
      window.removeEventListener('keydown', primeSfx);
    };
    window.addEventListener('pointerdown', primeSfx, { passive: true });
    window.addEventListener('touchstart', primeSfx, { passive: true });
    window.addEventListener('keydown', primeSfx, { once: false });

    // Drive wiring off the viewport width. We wire on !mobile and
    // unwire when the viewport becomes mobile so a desktop-sized
    // session narrowed into a phone profile stops booting playback
    // from incidental touches.
    const mql = window.matchMedia(MOBILE_QUERY);
    const syncFromViewport = () => {
      if (mql.matches) {
        unwireHandlers();
      } else {
        wireHandlers();
      }
    };
    syncFromViewport();
    const onMqlChange = (e: MediaQueryListEvent) => {
      if (e.matches) {
        unwireHandlers();
      } else {
        wireHandlers();
      }
    };
    mql.addEventListener('change', onMqlChange);

    const flushCursor = () => {
      getMusicPlayer().persistPlaybackCursor();
    };
    window.addEventListener('pagehide', flushCursor);
    window.addEventListener('beforeunload', flushCursor);

    return () => {
      cancelled = true;
      flushCursor();
      mql.removeEventListener('change', onMqlChange);
      window.removeEventListener('pagehide', flushCursor);
      window.removeEventListener('beforeunload', flushCursor);
      window.removeEventListener('pointerdown', primeSfx);
      window.removeEventListener('touchstart', primeSfx);
      window.removeEventListener('keydown', primeSfx);
      unwireHandlers();
      stopMuteCrossTabSync();
    };
  }, []);

  return null;
}
